/**
 * Shared validation rules for the Maybank Merchant prototype.
 * Anything that touches Email, Phone, Name, or Malaysian National ID
 * should import from here so rules stay consistent across screens.
 */
// ---------- Regexes ----------
/** Standard email — requires @, valid domain, supports .com.my and similar. */
export const EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/
/**
 * Malaysian-friendly name regex.
 * Accepts Unicode letters, spaces, and the symbols @ / - ' . , which appear
 * in Malaysian Indian/Muslim aliases and patronymics
 * (e.g. "Mohammad Ali @ Ah Meng", "Muthu a/l Samy").
 */
export const NAME_REGEX = /^[\p{L}\s@/\-',.]+$/u
/** Malaysian postal code: exactly 5 numeric digits. */
export const POSTAL_CODE_REGEX = /^\d{5}$/
// ---------- Email ----------
export const validateEmail = (raw: string): string | null => {
  const v = raw.trim()
  if (!v) return 'Email is required.'
  if (!EMAIL_REGEX.test(v))
    return 'Enter a valid email address (e.g. name@domain.com).'
  return null
}
// ---------- Name ----------
export const validateName = (raw: string): string | null => {
  const v = raw.trim()
  if (!v) return 'Full Legal Name is required.'
  if (v.length < 2) return 'Please enter your full legal name.'
  if (!NAME_REGEX.test(v))
    return "Use only letters, spaces, and the symbols @ / - ' . , (no numbers)."
  return null
}
// ---------- Malaysian Phone (+60) ----------
/** Strips non-digits and any leading 0. */
export const cleanPhoneMY = (raw: string): string =>
  raw.replace(/\D/g, '').replace(/^0+/, '')
/** Returns the digits only, capped at 10. Use this when binding to PhoneInput. */
export const sanitizePhoneMY = (raw: string): string =>
  raw.replace(/\D/g, '').slice(0, 10)
export const validatePhoneMY = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return 'Mobile number is required.'
  if (digits.length < 9 || digits.length > 10)
    return 'Enter a 9 or 10-digit Malaysian mobile number.'
  return null
}
// ---------- Date of Birth ----------
export const computeAge = (isoDate: string): number | null => {
  if (!isoDate) return null
  const dob = new Date(isoDate)
  if (Number.isNaN(dob.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const m = today.getMonth() - dob.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--
  return age
}
export const dobToYymmdd = (isoDate: string): string | null => {
  if (!isoDate) return null
  const dob = new Date(isoDate)
  if (Number.isNaN(dob.getTime())) return null
  const yy = String(dob.getFullYear()).slice(2)
  const mm = String(dob.getMonth() + 1).padStart(2, '0')
  const dd = String(dob.getDate()).padStart(2, '0')
  return `${yy}${mm}${dd}`
}
export const validateDob = (
  isoDate: string,
  { minAge = 18 }: { minAge?: number } = {},
): string | null => {
  if (!isoDate) return 'Date of Birth is required.'
  const dob = new Date(isoDate)
  if (Number.isNaN(dob.getTime())) return 'Enter a valid date.'
  if (dob >= new Date()) return 'Date of Birth must be in the past.'
  const age = computeAge(isoDate)
  if (age !== null && age < minAge)
    return `You must be at least ${minAge} years old.`
  return null
}
// ---------- Malaysian National ID (MyKad / MyPR / Police / Military) ----------
/** True when the input looks like a civilian MyKad/MyPR (digit-leading). */
export const isCivilianNricFormat = (raw: string): boolean => /^\d/.test(raw)
/** Auto-formats a civilian MyKad/MyPR as XXXXXX-XX-XXXX. */
export const formatNric = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 12)
  if (digits.length <= 6) return digits
  if (digits.length <= 8) return `${digits.slice(0, 6)}-${digits.slice(6)}`
  return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`
}
/**
 * Validate a Malaysian National ID. Handles civilian MyKad/MyPR (12 digits)
 * AND alphanumeric Police / Military authority cards (e.g. G/I/D/RF prefixes).
 * Optionally cross-checks the first 6 digits against a DOB.
 */
export const validateMalaysianId = (
  raw: string,
  { dob }: { dob?: string } = {},
): string | null => {
  const v = raw.trim()
  if (!v) return 'ID Number is required.'
  if (isCivilianNricFormat(v)) {
    const digits = v.replace(/\D/g, '')
    if (digits.length !== 12) return 'MyKad / MyPR must be exactly 12 digits.'
    if (dob) {
      const expected = dobToYymmdd(dob)
      if (expected && digits.slice(0, 6) !== expected)
        return 'Your ID number does not match your Date of Birth.'
    }
    return null
  }
  // Police / Military authority card — alphanumeric, letter-led, 5–15 chars
  if (!/^[A-Za-z][A-Za-z0-9]{4,14}$/.test(v))
    return 'Enter a valid Police or Military ID (alphanumeric, 5–15 characters).'
  return null
}
// ---------- Passport ----------
export const validatePassport = (raw: string): string | null => {
  const v = raw.trim()
  if (!v) return 'Passport Number is required.'
  if (!/^[A-Za-z0-9]{5,15}$/.test(v))
    return 'Enter a valid passport number (5–15 alphanumeric).'
  return null
}
// ---------- Postal Code ----------
export const validatePostalCodeMY = (raw: string): string | null => {
  if (!raw.trim()) return 'Postal Code is required.'
  if (!POSTAL_CODE_REGEX.test(raw.trim()))
    return 'Postal Code must be exactly 5 digits.'
  return null
}
